import { useEffect, useMemo } from 'react';
import * as Y from 'yjs';
import type { NodeChange } from '@xyflow/react';
import { canvasRoots, type CanvasBinding, TOP_LEVEL_BINDING } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';

const DEFAULT_THROTTLE_MS = 100;

/**
 * Apply React Flow NodeChange events back to the Yjs document.
 *
 * Pure flush — writes whatever it's given. The throttler upstream is
 * responsible for coalescing 60Hz pointer-rate position/dimensions writes
 * down to the server-safe ~10Hz before this function sees them.
 *
 * Change types handled:
 *  - position   → node.position plain-object replace (LWW, acceptable for drag)
 *  - dimensions → node.size plain-object replace
 *  - remove     → delete node from nodes map
 *  - select     → no-op (selection is local + awareness, not Yjs)
 *  - add/reset  → no-op (adds come through createNode; reset is RF-internal)
 */
/** Structural equality for a node `size` value ({ width, height }). */
function sameSize(stored: unknown, next: { width: number; height: number }): boolean {
  return (
    typeof stored === 'object' && stored !== null &&
    (stored as { width?: number }).width === next.width &&
    (stored as { height?: number }).height === next.height
  );
}

export function applyNodeChanges(doc: Y.Doc, changes: NodeChange[], origin: number, binding: CanvasBinding = TOP_LEVEL_BINDING): void {
  const nodes = canvasRoots(doc, binding).nodes;

  // Identify currently-collapsed frames at write time (not at change-emit
  // time) so we can drop position writes for their children. Without
  // this, RF's parent-extent clamp emits position changes against the
  // child's clamped 80×80-bound position; the throttler buffers them
  // for 100ms; meanwhile data.collapsed flips true; and when the
  // throttler finally flushes here, the corrupted positions land in
  // Yjs. Reading collapsed-state at the actual write boundary closes
  // that race regardless of how the changes got buffered.
  const collapsedFrameIds = new Set<string>();
  nodes.forEach((n) => {
    if (!(n instanceof Y.Map)) return;
    if (n.get('type') !== 'frame') return;
    const data = n.get('data');
    if (data instanceof Y.Map && data.get('collapsed') === true) {
      const id = n.get('id');
      if (typeof id === 'string') collapsedFrameIds.add(id);
    }
  });

  doc.transact(() => {
    for (const change of changes) {
      if (change.type === 'position') {
        const node = nodes.get(change.id);
        if (!node || !change.position) continue;
        // Drop position changes for children of collapsed frames — the
        // clamp would otherwise overwrite their authored coordinates.
        const parentId = node.get('parentId');
        if (typeof parentId === 'string' && collapsedFrameIds.has(parentId)) continue;
        node.set('position', change.position);
      } else if (change.type === 'dimensions') {
        const node = nodes.get(change.id);
        if (!node || !change.dimensions) continue;
        // Collapsed-frame dimensions writes are REDIRECTED to
        // data.collapsedSize instead of node.size — the user can resize
        // the folder while it's collapsed, and we want that to persist
        // independently of the expanded size. Reopening reads node.size
        // (the expanded layout); collapsing reads data.collapsedSize
        // (the folder dimensions). Both states remember their own size.
        if (collapsedFrameIds.has(change.id)) {
          const data = node.get('data');
          if (data instanceof Y.Map) {
            data.set('collapsedSize', change.dimensions);
          }
          continue;
        }
        // Skip the write when the size is unchanged. React Flow re-measures
        // every node on open and re-emits its current size; writing it
        // unconditionally mutates the Y.Doc, whose new state bytes flush and
        // bump entities.updated_at — re-sorting the canvas to "recently
        // updated" on every open with no edit (PRD-16).
        if (sameSize(node.get('size'), change.dimensions)) continue;
        node.set('size', change.dimensions);
      } else if (change.type === 'remove') {
        nodes.delete(change.id);
      }
      // select, add, reset → no-op
    }
  }, origin);
}

/**
 * Coalescing throttle for React Flow NodeChange events on their way to Yjs.
 *
 * Why: React Flow emits position changes at pointermove rate (~60Hz) during
 * drag and dimensions changes at the same rate during resize. Forwarding
 * every frame to Yjs blows past the server's per-conn rate limit
 * (200 frames / 10s = 20Hz) within ~3 seconds, tripping a disconnect.
 *
 * What it does: buffers incoming changes keyed by `(type, id)` so the latest
 * position/dimensions for a given node replaces any earlier pending entry.
 * Flushes the buffer at most every `windowMs` (default 100ms = 10Hz, matching
 * the lexical bridge). Settled events (add/remove/replace) ride the same
 * flush — they're not throttled or deduped, but they don't fire at pointer
 * rate either.
 */
export class NodeChangeThrottler {
  /**
   * Pending changes keyed by `${type}:${id}`. Position and dimensions
   * collapse onto the latest entry; settled changes (remove) overwrite by
   * key too, which is fine because removing the same node twice is a no-op.
   * Insertion order is preserved, so a position write followed by a remove
   * for the same node serialises as remove-only (Map keeps the last set).
   */
  private pending = new Map<string, NodeChange>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  constructor(
    private readonly flush: (changes: NodeChange[]) => void,
    private readonly windowMs: number = DEFAULT_THROTTLE_MS,
  ) {}

  push(changes: NodeChange[]): void {
    if (this.destroyed) return;
    for (const c of changes) {
      this.pending.set(this.keyFor(c), c);
    }
    if (this.timer === null) {
      this.timer = setTimeout(() => this.drain(), this.windowMs);
    }
  }

  /**
   * Drain the buffer immediately — used at drag-end / resize-end so the
   * final settled value isn't held by the next throttle window.
   */
  flushImmediate(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.drain();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.pending.clear();
  }

  private drain(): void {
    this.timer = null;
    if (this.pending.size === 0) return;
    const out = Array.from(this.pending.values());
    this.pending.clear();
    this.flush(out);
  }

  private keyFor(change: NodeChange): string {
    // RF NodeChange shapes have an `id` for everything except 'add' and
    // 'replace'. For those we key on the inner item id so adds/replaces of
    // the same node coalesce too.
    if (change.type === 'add' || change.type === 'replace') {
      return `${change.type}:${change.item.id}`;
    }
    return `${change.type}:${change.id}`;
  }
}

export function useYjsNodeSync(doc: Y.Doc, origin: number) {
  const binding = useCanvasBinding();
  const throttler = useMemo(
    () => new NodeChangeThrottler((changes) => applyNodeChanges(doc, changes, origin, binding)),
    [doc, origin, binding],
  );

  // Drain on unmount so a tab-close mid-drag still persists the last position.
  useEffect(() => {
    return () => {
      throttler.flushImmediate();
      throttler.destroy();
    };
  }, [throttler]);

  return {
    onNodesChange: (changes: NodeChange[]) => throttler.push(changes),
    /** Caller invokes this on drag-end / resize-end to flush the buffer right away. */
    flushImmediate: () => throttler.flushImmediate(),
  };
}
